import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { Col, Container, Row, Spinner, Tab, Tabs, Table, Form, InputGroup } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import Category from '../Category';
import Footer from '../Footer';
import Header from '../Header';
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardContent } from '@mui/material';
import ApiFetch from '../ApiFetch';
import CommonDialog from '../common/CommonDialog';
import Constants from '../common/Constants';
import { LoginInfo } from '../../App';
import Papa from 'papaparse';
import { Link } from 'react-router-dom';

const AdminPayouts = () => {
    // 管理者向けAPI
    async function getAllUsersRevenue() {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/payouts/revenueAll").then((r) => r.json().then((json) => {
            return json
        }))
    }

    async function getPayoutsHistory() {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/payouts/payoutsAll").then((r) => r.json().then((json) => {
            return json
        }))
    }

    async function processPayouts(userIds) {
        return await new ApiFetch().post(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/payment/process-payouts", { userIds: userIds }).then((r) => r.json().then((json) => {
            return json
        }))
    }

    async function getEnableCoinAll() {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/coin/unusedBalance").then((r) => r.json().then((json) => {
            return json
        }))
    }

    async function getCoinAll() {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/coin/coinAll").then((r) => r.json().then((json) => {
            return json
        }))
    }

    const { t, i18n } = useTranslation()
    // ログイン情報
    const { loginInfo, setLoginInfo } = useContext(LoginInfo)
    const [loading, setLoading] = useState(true)
    const [usersRevenue, setUsersRevenue] = useState([])
    const [payoutsHistory, setPayoutsHistory] = useState([])
    const [selectedUsers, setSelectedUsers] = useState([])
    const [selectAll, setSelectAll] = useState(false)
    const [showProcessConfirm, setShowProcessConfirm] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [minBalanceFilter, setMinBalanceFilter] = useState(5000) // デフォルトは5000円以上
    const [showExportSuccess, setShowExportSuccess] = useState(false)
    const [unusedBalance, setUnusedBalance] = useState(0)
    const [coinAll, setCoinAll] = useState(0)

    // Fetch data on component mount
    useEffect(() => {
        document.title = "管理者 - 出金管理 - アワートAI"
        
        // Get all users' revenue data
        getAllUsersRevenue().then(r => {
            if (r.result) {
                setUsersRevenue(r.data.list)
            }
            setLoading(false)
        })

        // Get payouts history
        getPayoutsHistory().then(r => {
            if (r.result) {
                setPayoutsHistory(r.data)
            }
        })

        getEnableCoinAll().then(r => {
            if (r.result) {
                setUnusedBalance(r.data.coin)
            }
        })

        getCoinAll().then(r => {
            if (r.result) {
                setCoinAll(r.data.coin)
            }
        })
    }, [])

    // Handle select all checkbox
    const handleSelectAll = () => {
        if (!selectAll) {
            // Filter users meeting the minimum balance criteria
            const eligibleUsers = filteredUsers.filter(user => user.balance >= minBalanceFilter).map(user => user.user_id)
            setSelectedUsers(eligibleUsers)
        } else {
            setSelectedUsers([])
        }
        setSelectAll(!selectAll)
    }

    // Handle individual user selection
    const handleSelectUser = (userId, balance) => {
        if (balance < minBalanceFilter) return // Don't allow selection if balance is below minimum

        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId))
        } else {
            setSelectedUsers([...selectedUsers, userId])
        }
    }

    // Process CSV export
    const exportCSV = () => {
        // Get only the selected users
        const selectedUsersData = usersRevenue.filter(user => selectedUsers.includes(user.user_id))

        // Format data for CSV
        const csvData = selectedUsersData.map(user => ({
            ユーザーID: user.id,
            ユーザー名: user.username,
            残高: user.balance,
            銀行名: user.bank_name || "",
            支店名: user.branch_name || "",
            口座種別: user.account_type || "",
            口座番号: user.account_number || "",
            口座名義: user.account_holder || "",
            出金予定額: user.balance
        }))

        // Create CSV
        const csv = Papa.unparse(csvData)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        
        // Create filename with current date
        const today = new Date()
        const formattedDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`
        
        link.setAttribute('href', url)
        link.setAttribute('download', `payouts_${formattedDate}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        setShowExportSuccess(true)
        setTimeout(() => setShowExportSuccess(false), 3000)
    }

    // Process payments
    const handleProcessPayments = () => {
        setShowProcessConfirm(false)
        // In a real implementation, this would trigger the payment process
        processPayouts(selectedUsers).then(r => {
            if (r.result) {
                // Refresh data
                getAllUsersRevenue().then(r => {
                    if (r.result) {
                        setUsersRevenue(r.data)
                    }
                })
                
                getPayoutsHistory().then(r => {
                    if (r.result) {
                        setPayoutsHistory(r.data)
                    }
                })
                
                // Clear selections
                setSelectedUsers([])
                setSelectAll(false)
            } else {
                alert("処理エラー: " + r.errors)
            }
        })
    }

    // Filter users based on search and minimum balance
    const filteredUsers = usersRevenue.filter(user => 
        (user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
         user.user_id?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (!minBalanceFilter || user.balance >= minBalanceFilter)
    )

    return (
        <div className="wrapper" style={{ backgroundColor: "#EEEEEE", borderRadius: "10px" }}>
            <header className="App-header"></header>
            <Header />
            <Category />
            <CommonDialog open={showProcessConfirm}
                onClick={handleProcessPayments} onClose={() => setShowProcessConfirm(false)}
                title="選択したユーザーに出金処理を行いますか？" buttonLabel="処理実行" buttonColor="primary" showCancelbutton={true} />
            <Container className='w-100'>
                <h4>管理者 - 出金管理</h4>

                {/* Summary Stats */}
                <Row className='mb-4 text-left p-3' style={{ backgroundColor: "#FFFFFF" }}>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>総入金額</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>
                                {Constants.CURRENCY_UNIT_LEFT}
                                {coinAll
                                    .toLocaleString()}
                            </div>
                        )}
                    </Col>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>総ユーザー数</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>{usersRevenue.length.toLocaleString()}</div>
                        )}
                    </Col>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>出金対象ユーザー数</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>
                                {usersRevenue.filter(user => user.balance >= 5000).length.toLocaleString()}
                            </div>
                        )}
                    </Col>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>未使用残高</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>
                                {Constants.CURRENCY_UNIT_LEFT}
                                {unusedBalance
                                    .toLocaleString()}
                            </div>
                        )}
                    </Col>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>出金対象総額</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>
                                {Constants.CURRENCY_UNIT_LEFT}
                                {usersRevenue
                                    .filter(user => user.balance >= 5000)
                                    .reduce((sum, user) => sum + user.balance, 0)
                                    .toLocaleString()}
                            </div>
                        )}
                    </Col>
                    <Col xs={6} sm={6} md={3} lg={2} xl={2} xxl={2}>
                        <div>選択ユーザー出金額</div>
                        {loading ? <Spinner size='sm' /> : (
                            <div style={{ fontWeight: "bold", fontSize: "36px" }}>
                                {Constants.CURRENCY_UNIT_LEFT}
                                {usersRevenue
                                    .filter(user => selectedUsers.includes(user.id))
                                    .reduce((sum, user) => sum + user.balance, 0)
                                    .toLocaleString()}
                            </div>
                        )}
                    </Col>
                </Row>

                {/* Filters and Actions */}
                <Row className='mb-4 text-left p-3' style={{ backgroundColor: "#FFFFFF" }}>
                    <Col xs={12} sm={12} md={6} lg={4} xl={4} xxl={4} className="mb-3">
                        <InputGroup>
                            <InputGroup.Text><Icon.Search /></InputGroup.Text>
                            <Form.Control
                                placeholder="ユーザー名またはIDで検索"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={4} xl={4} xxl={4} className="mb-3">
                        <InputGroup>
                            <InputGroup.Text>最低残高</InputGroup.Text>
                            <Form.Control
                                type="number"
                                min="0"
                                step="1000"
                                value={minBalanceFilter}
                                onChange={(e) => setMinBalanceFilter(Number(e.target.value))}
                            />
                            <InputGroup.Text>{Constants.CURRENCY_UNIT_LEFT}</InputGroup.Text>
                        </InputGroup>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4} xl={4} xxl={4} className="d-flex justify-content-end mb-3">
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            className="me-2"
                            disabled={selectedUsers.length === 0}
                            onClick={exportCSV}
                        >
                            <Icon.FileEarmarkArrowDown className="me-1" />
                            出金用CSV出力
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary"
                            disabled={selectedUsers.length === 0}
                            onClick={() => setShowProcessConfirm(true)}
                        >
                            <Icon.CurrencyYen className="me-1" />
                            出金処理実行
                        </Button>
                    </Col>
                </Row>

                {showExportSuccess && (
                    <Alert severity="success" className="mb-3">
                        CSVが正常に出力されました
                    </Alert>
                )}

                <Tabs defaultActiveKey={"users"} onSelect={() => {}}>
                    <Tab eventKey="users" title="ユーザー残高一覧">
                        <Row className='mb-4 text-center p-3' style={{ backgroundColor: "#FFFFFF" }}>
                            <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                                <div className="table-responsive">
                                    <Table striped bordered hover style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: "50px" }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectAll}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                                <th>ユーザーID</th>
                                                <th>ユーザー名</th>
                                                <th>有料会員数</th>
                                                <th>月額</th>
                                                <th>現在残高</th>
                                                <th>銀行口座</th>
                                                <th>本人確認</th>
                                                <th>SMS認証</th>
                                                <th>状態</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center">
                                                        <Spinner animation="border" />
                                                    </td>
                                                </tr>
                                            ) : filteredUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center">
                                                        該当するユーザーはいません
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredUsers.map(user => (
                                                    <tr key={user.user_id} className={user.balance >= minBalanceFilter ? "" : "text-muted"}>
                                                        <td>
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={selectedUsers.includes(user.user_id)}
                                                                onChange={() => handleSelectUser(user.user_id, user.balance)}
                                                                disabled={user.balance < minBalanceFilter}
                                                            />
                                                        </td>
                                                        <td><Link target='_blank' to={"/user/"+user.user_id}>{user.user_id}</Link></td>
                                                        <td>{user.user_name}</td>
                                                        <td className="text-end">
                                                            {user.count.toLocaleString()}
                                                        </td>
                                                        <td className="text-end">
                                                            {Constants.CURRENCY_UNIT_LEFT}{(user.amount*user.count).toLocaleString()}
                                                        </td>
                                                        <td className="text-end">
                                                            {Constants.CURRENCY_UNIT_LEFT}{user.balance.toLocaleString()}
                                                        </td>
                                                        <td>
                                                            {user.bank_name ? (
                                                                <Icon.CheckCircle style={{ color: "green" }} />
                                                            ) : (
                                                                <Icon.XCircle style={{ color: "red" }} />
                                                            )}
                                                        </td>
                                                        <td>
                                                            {user.id_auth === 1 ? (
                                                                <Icon.CheckCircle style={{ color: "green" }} />
                                                            ) : (
                                                                <Icon.XCircle style={{ color: "red" }} />
                                                            )}
                                                        </td>
                                                        <td>
                                                            {user.sms_auth === 1 ? (
                                                                <Icon.CheckCircle style={{ color: "green" }} />
                                                            ) : (
                                                                <Icon.XCircle style={{ color: "red" }} />
                                                            )}
                                                        </td>
                                                        <td>
                                                            {user.bank_name && user.id_auth === 1 && user.sms_auth === 1 ? (
                                                                user.balance >= 5000 ? (
                                                                    <span className="text-success">出金対象</span>
                                                                ) : (
                                                                    <span>残高不足</span>
                                                                )
                                                            ) : (
                                                                <span className="text-danger">出金不可</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </Tab>
                    <Tab eventKey="history" title="出金履歴">
                        <Row className='mb-4 text-center p-3' style={{ backgroundColor: "#FFFFFF" }}>
                            <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                                <div className="table-responsive">
                                    <Table striped bordered hover style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th>処理日時</th>
                                                <th>ユーザーID</th>
                                                <th>ユーザー名</th>
                                                <th>金額</th>
                                                <th>処理状況</th>
                                                <th>担当管理者</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payoutsHistory.list === undefined || payoutsHistory.list.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center">
                                                        履歴はありません
                                                    </td>
                                                </tr>
                                            ) : (
                                                payoutsHistory.list.map((payout, index) => (
                                                    <tr key={index}>
                                                        <td>{payout.ymdhi}</td>
                                                        <td>{payout.user_id}</td>
                                                        <td>{payout.user_name}</td>
                                                        <td className="text-end">
                                                            {Constants.CURRENCY_UNIT_LEFT}{payout.amount.toLocaleString()}
                                                            <br />
                                                            <span style={{ fontSize: "14px", color: "#555555" }}>
                                                                (内振込手数料{Constants.CURRENCY_UNIT_LEFT}{payout.fee.toLocaleString()})
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {payout.status === "1" ? (
                                                                <span className="text-success">処理完了</span>
                                                            ) : (
                                                                <span className="text-warning">処理中</span>
                                                            )}
                                                        </td>
                                                        <td>{payout.admin_name}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </Tab>
                </Tabs>
            </Container>
            <Footer />
        </div>
    )
}

export default AdminPayouts;